import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { updateUserProfile, changeUsername } from '../services/userService';
import {
  requestAccountDeletion,
  deleteAccountImmediate,
} from '../services/accountService';
import './AccountPage.css';

const USERNAME_REGEX = /^[a-zA-Z0-9_]{3,20}$/;

export default function AccountPage() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const isDark = !!userProfile?.darkMode;

  // Username change
  const [newUsername, setNewUsername]     = useState('');
  const [usernameMsg, setUsernameMsg]     = useState('');
  const [usernameLoading, setUsernameLoading] = useState(false);

  // Password reset
  const [pwMsg, setPwMsg]                 = useState('');
  const [pwLoading, setPwLoading]         = useState(false);

  // Delete account
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError]     = useState('');

  async function handleUsernameChange(e) {
    e.preventDefault();
    const trimmed = newUsername.trim();
    if (!trimmed || usernameLoading) return;
    if (!USERNAME_REGEX.test(trimmed)) {
      setUsernameMsg('3–20 characters: letters, numbers, underscores only.');
      return;
    }
    setUsernameLoading(true);
    setUsernameMsg('');
    try {
      await changeUsername(currentUser.uid, userProfile.username, trimmed);
      setUsernameMsg('Username updated!');
      setNewUsername('');
    } catch (err) {
      setUsernameMsg(err.message);
    } finally {
      setUsernameLoading(false);
    }
  }

  async function handleToggleDarkMode() {
    try {
      await updateUserProfile(currentUser.uid, { darkMode: !isDark });
    } catch (err) {
      console.error('dark mode update error:', err);
    }
  }

  async function handlePasswordReset() {
    if (pwLoading) return;
    setPwLoading(true);
    setPwMsg('');
    try {
      await sendPasswordResetEmail(auth, currentUser.email);
      setPwMsg(`Reset email sent to ${currentUser.email}`);
    } catch {
      setPwMsg('Failed to send reset email. Try again.');
    } finally {
      setPwLoading(false);
    }
  }

  async function handleSignOut() {
    try {
      await signOut(auth);
    } catch (err) {
      console.error('sign out error:', err);
    }
    navigate('/', { replace: true });
  }

  async function handleDeleteAccount() {
    setDeleteLoading(true);
    setDeleteError('');
    try {
      const { getDocs, collection, query, where } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      const snap = await getDocs(
        query(collection(db, 'leagues'), where('commissionerId', '==', currentUser.uid))
      );
      if (snap.docs.length > 0) {
        await requestAccountDeletion(currentUser.uid);
      } else {
        await deleteAccountImmediate(currentUser);
        navigate('/', { replace: true });
        return;
      }
      setShowDeleteConfirm(false);
      navigate('/leagues', { replace: true });
    } catch (err) {
      if (err.code === 'auth/requires-recent-login') {
        setDeleteError('Please sign out and sign back in before deleting your account.');
      } else {
        setDeleteError('Something went wrong. Please try again.');
      }
    } finally {
      setDeleteLoading(false);
    }
  }

  return (
    <div className={`acct-page${isDark ? ' dark' : ''}`}>
      <header className="acct-header">
        <button className="acct-back" onClick={() => navigate(-1)}>← Back</button>
        <span className="acct-header-title display-font">My Account</span>
      </header>

      <div className="acct-body">

        {/* ── Profile info ── */}
        <section className="acct-section">
          <div className="acct-section-title">Profile</div>
          <div className="acct-info-row">
            <span className="acct-info-label">Username</span>
            <span className="acct-info-value">@{userProfile?.username}</span>
          </div>
          <div className="acct-info-row">
            <span className="acct-info-label">Email</span>
            <span className="acct-info-value">{currentUser?.email}</span>
          </div>
        </section>

        {/* ── Change username ── */}
        <section className="acct-section">
          <div className="acct-section-title">Change Username</div>
          <form className="acct-form" onSubmit={handleUsernameChange}>
            <input
              className="acct-input"
              type="text"
              placeholder="New username"
              value={newUsername}
              onChange={(e) => { setNewUsername(e.target.value); setUsernameMsg(''); }}
              maxLength={20}
              autoComplete="off"
            />
            <button
              className="acct-btn acct-btn--primary"
              type="submit"
              disabled={!newUsername.trim() || usernameLoading}
            >
              {usernameLoading ? 'Saving…' : 'Save'}
            </button>
          </form>
          {usernameMsg && (
            <p className={`acct-msg${usernameMsg === 'Username updated!' ? ' acct-msg--ok' : ' acct-msg--err'}`}>
              {usernameMsg}
            </p>
          )}
        </section>

        {/* ── Preferences ── */}
        <section className="acct-section">
          <div className="acct-section-title">Preferences</div>
          <div className="acct-pref-row">
            <span className="acct-pref-label">Dark Mode</span>
            <button
              className={`acct-toggle${isDark ? ' acct-toggle--on' : ''}`}
              onClick={handleToggleDarkMode}
              aria-label="Toggle dark mode"
            >
              <span className="acct-toggle-knob" />
            </button>
          </div>
        </section>

        {/* ── Security ── */}
        <section className="acct-section">
          <div className="acct-section-title">Security</div>
          <button
            className="acct-btn acct-btn--secondary"
            onClick={handlePasswordReset}
            disabled={pwLoading}
          >
            {pwLoading ? 'Sending…' : 'Send Password Reset Email'}
          </button>
          {pwMsg && <p className="acct-msg acct-msg--ok">{pwMsg}</p>}
        </section>

        {/* ── Sign out ── */}
        <section className="acct-section">
          <button className="acct-btn acct-btn--secondary" onClick={handleSignOut}>
            Sign Out
          </button>
        </section>

        {/* ── Danger zone ── */}
        <section className="acct-section acct-section--danger">
          <div className="acct-section-title">Danger Zone</div>
          {!showDeleteConfirm ? (
            <button
              className="acct-btn acct-btn--danger"
              onClick={() => { setDeleteError(''); setShowDeleteConfirm(true); }}
            >
              Delete Account
            </button>
          ) : (
            <div className="acct-delete-confirm">
              <p className="acct-delete-warning">
                This will permanently delete your account. If you are a league commissioner,
                your leagues will be deleted after a 48-hour grace period.
              </p>
              {deleteError && <p className="acct-msg acct-msg--err">{deleteError}</p>}
              <div className="acct-delete-btns">
                <button
                  className="acct-btn acct-btn--danger"
                  onClick={handleDeleteAccount}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? 'Processing…' : 'Confirm Delete'}
                </button>
                <button
                  className="acct-btn acct-btn--secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>

      </div>
    </div>
  );
}
